import { useState } from 'react';
import { Steuerbox, SteuerboxType, Steuerpfad, TechnischeRessource } from '../../../types/network';
import { PlusIcon, TrashIcon, ChevronIcon, LinkIcon } from '../../icons/ButtonIcons';
import { useReadOnlyUi } from '../../../store/featureStore';
import { parseUtcTimestamp, formatInstantLabel } from '../../../utils/timeUtils';
import {
  BOX_TYPE_LABELS,
  CODING_LABELS,
  STEUERPFAD_LABELS,
  formatCalendarDate,
  formatWattAsKw,
  isInstalled,
  isSynced,
} from './controlMasterDataLabels';
import { Fact, SourceBadge, TakeoverHint } from './ControlMasterDataParts';

interface SteuerboxSectionProps {
  steuerboxes: Steuerbox[];
  technischeRessourcen: TechnischeRessource[];
  /** Gateway serials of the house's SMGWs - suggestions for the box's SMGW field. */
  smgwSerials?: string[];
  onUpdate: (steuerboxes: Steuerbox[]) => void;
}

const srTypeLabels: Record<string, string> = {
  pv_anlage: 'PV Anlage',
  battery_storage: 'Batteriespeicher',
  heat_pump: 'Wärmepumpe',
  wallbox: 'Wallbox',
  air_conditioning: 'Klimaanlage',
  other: 'Sonstige Einrichtung',
};

const inputClass = 'w-full px-2 py-1 text-[13px] border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-600 text-gray-900 dark:text-white';
const labelClass = 'text-[11px] text-gray-500 dark:text-primary-400 block mb-1';

export const SteuerboxSection = ({ steuerboxes, technischeRessourcen, smgwSerials = [], onUpdate }: SteuerboxSectionProps) => {
  // Read-only deployments: no add/edit/delete controls; an empty section is
  // pure noise there and disappears entirely.
  const readOnly = useReadOnlyUi();
  const [isExpanded, setIsExpanded] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);

  const addSteuerbox = (type: SteuerboxType) => {
    const newSteuerbox: Steuerbox = {
      id: crypto.randomUUID(),
      type,
      linkedTechnicalResourceIds: [],
      source: 'manual',
      steuerpfad: type === 'digital' ? 'ems' : 'relay',
      ...(type !== 'digital' ? { numRelays: 4 } : {})
    };
    onUpdate([...steuerboxes, newSteuerbox]);
    setIsExpanded(true);
    setEditingId(newSteuerbox.id); // Auto-edit new steuerbox
  };

  const removeSteuerbox = (id: string) => {
    onUpdate(steuerboxes.filter(box => box.id !== id));
  };

  /**
   * Every edit of a synced box takes it over into manual care (source = manual), otherwise the next
   * master-data sync would overwrite the correction (plans/steuerung-cls.md 3.6, ownership rule).
   */
  const updateSteuerbox = (id: string, updates: Partial<Steuerbox>) => {
    onUpdate(steuerboxes.map(box =>
      box.id === id ? { ...box, ...updates, ...(isSynced(box.source) ? { source: 'manual' as const } : {}) } : box
    ));
  };

  const toggleResourceLink = (steuerboxId: string, resourceId: string) => {
    const box = steuerboxes.find(b => b.id === steuerboxId);
    if (!box) return;

    const linkedIds = box.linkedTechnicalResourceIds || [];
    const newLinkedIds = linkedIds.includes(resourceId)
      ? linkedIds.filter(id => id !== resourceId)
      : [...linkedIds, resourceId];

    updateSteuerbox(steuerboxId, { linkedTechnicalResourceIds: newLinkedIds });
  };

  // Get TRs that are already linked to other steuerboxes
  const getLinkedResourceIds = (excludeBoxId: string): Set<string> => {
    const linkedIds = new Set<string>();
    steuerboxes.forEach(box => {
      if (box.id !== excludeBoxId && isInstalled(box)) {
        (box.linkedTechnicalResourceIds || []).forEach(id => linkedIds.add(id));
      }
    });
    return linkedIds;
  };

  const installed = steuerboxes.filter(isInstalled);
  const removed = steuerboxes.filter(b => !isInstalled(b));

  if (readOnly && steuerboxes.length === 0) {
    return null;
  }

  const renderBox = (box: Steuerbox) => {
    const isEditing = editingId === box.id;
    const typeInfo = BOX_TYPE_LABELS[box.type] ?? BOX_TYPE_LABELS.digital;
    const linkedResources = technischeRessourcen.filter(tr => (box.linkedTechnicalResourceIds || []).includes(tr.id));
    const alreadyLinkedByOthers = getLinkedResourceIds(box.id);
    const boxRemoved = !isInstalled(box);
    const channels = box.channels ?? [];
    const subtitle = [
      box.type === 'digital' ? typeInfo.subtitle : `${typeInfo.subtitle} (${box.numRelays || 4} Relais)`,
      linkedResources.length > 0 ? `${linkedResources.length} TR` : null,
    ].filter(Boolean).join(' · ');

    return (
      <div key={box.id} className={`bg-gray-50 dark:bg-primary-700/50 rounded-md border border-gray-100 dark:border-primary-600 overflow-hidden ${boxRemoved ? 'opacity-60' : ''}`}>
        {/* Header */}
        <div className="flex items-center px-2 py-1.5">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className={`w-2 h-2 rounded-full shrink-0 ${typeInfo.dot}`}></div>
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-[13px] text-gray-900 dark:text-white truncate">{box.name || typeInfo.title}</span>
                <SourceBadge source={box.source} />
                {box.isOnline != null && (
                  <span
                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${box.isOnline ? 'bg-green-500' : 'bg-red-500'}`}
                    title={`${box.isOnline ? 'Box erreichbar' : 'Box nicht erreichbar'}${box.lastSeenOnlineAt ? ` · letzter Kontakt ${formatInstantLabel(parseUtcTimestamp(box.lastSeenOnlineAt))}` : ''} (maas)`}
                  />
                )}
              </div>
              <span className="text-[10px] text-gray-500 dark:text-primary-400 truncate">
                {!box.serialNumber && 'ohne Seriennummer · '}
                {box.serialNumber && !(box.name ?? '').includes(box.serialNumber) && <><span className="font-mono">{box.serialNumber}</span>{' · '}</>}
                {subtitle}
                {boxRemoved && ` · ausgebaut ${formatCalendarDate(box.removedAt) ?? ''}`}
              </span>
            </div>
          </div>
          {!readOnly && (
          <div className="flex items-center gap-1 shrink-0 ml-1">
            <button
              onClick={() => setEditingId(isEditing ? null : box.id)}
              className={`p-1 rounded ${isEditing ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-primary-400 hover:bg-gray-200 dark:hover:bg-primary-600'}`}
              title={isEditing ? 'Fertig' : 'Bearbeiten'}
            >
              {isEditing ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              )}
            </button>
            <button
              onClick={() => removeSteuerbox(box.id)}
              className="text-error-600 dark:text-error-400 hover:bg-error-500/10 p-1 rounded"
              title={isSynced(box.source) ? 'Steuerbox löschen (der nächste Abgleich legt sie wieder an, solange die Quelle sie führt)' : 'Steuerbox löschen'}
            >
              <TrashIcon />
            </button>
          </div>
          )}
        </div>

        {/* Edit Mode */}
        {isEditing && (
          <div className="border-t border-gray-200 dark:border-primary-600 p-2 space-y-2">
            <TakeoverHint source={box.source} />
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <label className={labelClass}>Name (optional)</label>
                <input
                  type="text"
                  value={box.name || ''}
                  onChange={(e) => updateSteuerbox(box.id, { name: e.target.value || undefined })}
                  placeholder={typeInfo.title}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Art</label>
                <select
                  value={box.type}
                  onChange={(e) => updateSteuerbox(box.id, { type: e.target.value as SteuerboxType })}
                  className={inputClass}
                  aria-label="Art der Steuerbox"
                >
                  {(Object.keys(BOX_TYPE_LABELS) as SteuerboxType[]).map(t => (
                    <option key={t} value={t}>{BOX_TYPE_LABELS[t].title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Steuerpfad</label>
                <select
                  value={box.steuerpfad ?? 'unknown'}
                  onChange={(e) => updateSteuerbox(box.id, { steuerpfad: e.target.value as Steuerpfad })}
                  className={inputClass}
                  aria-label="Steuerpfad"
                >
                  {(Object.keys(STEUERPFAD_LABELS) as Steuerpfad[]).map(p => (
                    <option key={p} value={p}>{STEUERPFAD_LABELS[p]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Seriennummer (Steuerbox-ID)</label>
                <input
                  type="text"
                  value={box.serialNumber || ''}
                  onChange={(e) => updateSteuerbox(box.id, { serialNumber: e.target.value.trim() || undefined })}
                  placeholder="z. B. GSWIC025070007"
                  className={`${inputClass} font-mono`}
                />
              </div>
              <div>
                <label className={labelClass}>SMGW</label>
                <input
                  type="text"
                  list={`smgw-${box.id}`}
                  value={box.smgwGatewayId || ''}
                  onChange={(e) => updateSteuerbox(box.id, { smgwGatewayId: e.target.value.trim() || undefined })}
                  placeholder="Gateway-Seriennummer"
                  className={`${inputClass} font-mono`}
                />
                <datalist id={`smgw-${box.id}`}>
                  {smgwSerials.map(s => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div>
                <label className={labelClass}>FNN-Version</label>
                <input
                  type="text"
                  value={box.fnnVersion || ''}
                  onChange={(e) => updateSteuerbox(box.id, { fnnVersion: e.target.value.trim() || undefined })}
                  placeholder="z. B. 1.5"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Hersteller</label>
                <input
                  type="text"
                  value={box.manufacturer || ''}
                  onChange={(e) => updateSteuerbox(box.id, { manufacturer: e.target.value || undefined })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Einbau</label>
                <input
                  type="date"
                  value={box.installedAt ?? ''}
                  onChange={(e) => updateSteuerbox(box.id, { installedAt: e.target.value || undefined })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Ausbau</label>
                <input
                  type="date"
                  value={box.removedAt ?? ''}
                  onChange={(e) => updateSteuerbox(box.id, { removedAt: e.target.value || undefined })}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Linked Technical Resources */}
            <div>
              <label className={`${labelClass} flex items-center gap-1`}>
                <LinkIcon className="w-3 h-3" />
                Verknüpfte Technische Ressourcen
              </label>
              {technischeRessourcen.length === 0 ? (
                <p className="text-[12px] text-gray-400 dark:text-primary-500 italic">
                  Keine technischen Ressourcen vorhanden
                </p>
              ) : (
                <div className="space-y-1">
                  {technischeRessourcen.map(tr => {
                    const isLinked = (box.linkedTechnicalResourceIds || []).includes(tr.id);
                    const isLinkedToOther = alreadyLinkedByOthers.has(tr.id);
                    const typeLabel = srTypeLabels[tr.type] || tr.type;

                    return (
                      <button
                        key={tr.id}
                        onClick={() => !isLinkedToOther && toggleResourceLink(box.id, tr.id)}
                        disabled={isLinkedToOther}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-[12px] transition-colors ${
                          isLinkedToOther
                            ? 'bg-gray-100 dark:bg-primary-800 text-gray-400 dark:text-primary-500 cursor-not-allowed'
                            : isLinked
                              ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300'
                              : 'bg-white dark:bg-primary-600 border border-gray-200 dark:border-primary-500 text-gray-700 dark:text-primary-200 hover:border-blue-300 dark:hover:border-blue-600'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                          isLinked
                            ? 'bg-blue-500 border-blue-500 text-white'
                            : 'border-gray-300 dark:border-primary-500'
                        }`}>
                          {isLinked && (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <span className="flex-1 truncate">{tr.name || typeLabel}</span>
                        <span className="text-[10px] text-gray-400 dark:text-primary-500 shrink-0">
                          {tr.maxPower} kW
                        </span>
                        {isLinkedToOther && (
                          <span className="text-[10px] text-orange-500 dark:text-orange-400 shrink-0">
                            (andere Steuerbox)
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* View Mode - master data, CLS blocks and linked resources */}
        {!isEditing && (
          <div className="border-t border-gray-200 dark:border-primary-600 px-2 py-1.5 space-y-1">
            <Fact label="SMGW" value={box.smgwGatewayId} mono />
            <Fact label="Hersteller" value={[box.manufacturer, box.model].filter(Boolean).join(' · ') || null} />
            <Fact label="FNN-Version" value={box.fnnVersion} />
            <Fact label="Steuerpfad" value={box.steuerpfad && box.steuerpfad !== 'unknown' ? STEUERPFAD_LABELS[box.steuerpfad] : null} />
            <Fact label="Einbau" value={formatCalendarDate(box.installedAt)} />
            {channels.map(ch => (
              <Fact
                key={ch.id}
                label={`CLS ${ch.clsIndex}`}
                value={[
                  CODING_LABELS[ch.coding] ?? ch.coding,
                  ch.minPowerW != null || ch.maxPowerW != null ? `${formatWattAsKw(ch.minPowerW) ?? '?'} – ${formatWattAsKw(ch.maxPowerW) ?? '?'}` : null,
                  ch.srId ? `SR ${ch.srId}` : null,
                ].filter(Boolean).join(' · ')}
              />
            ))}
            {linkedResources.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-0.5">
                {linkedResources.map(tr => (
                  <span
                    key={tr.id}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-[10px] rounded"
                  >
                    {tr.name || srTypeLabels[tr.type] || tr.type}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-400 dark:bg-indigo-500" />
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left pl-4 pr-3 py-2.5 hover:bg-gray-50 dark:hover:bg-primary-700/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="shrink-0 w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Steuerbox</span>
          {installed.length > 0 && (
            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">{installed.length}</span>
          )}
        </div>
        <ChevronIcon
          direction={isExpanded ? "up" : "down"}
          className="w-4 h-4 text-gray-400 dark:text-gray-500"
        />
      </button>

      {isExpanded && (
        <div className="pl-4 pr-3 pb-3">
          <div className="space-y-1.5 mb-2">
            {installed.map(renderBox)}
            {removed.length > 0 && (
              <button
                onClick={() => setShowRemoved(!showRemoved)}
                className="text-[11px] text-gray-500 dark:text-primary-400 hover:underline"
              >
                {showRemoved ? 'Ausgebaute Steuerboxen ausblenden' : `${removed.length} ausgebaute Steuerbox${removed.length === 1 ? '' : 'en'} anzeigen`}
              </button>
            )}
            {showRemoved && removed.map(renderBox)}
          </div>
          {!readOnly && (
          <div className="flex gap-1.5">
            <button
              onClick={() => addSteuerbox('analog')}
              className="flex-1 text-[#4285f4] dark:text-accent-primary hover:bg-[#4285f4]/10 dark:hover:bg-accent-primary/10 border border-[#4285f4]/30 dark:border-accent-primary/30 h-7 flex items-center justify-center gap-1 rounded-md text-xs"
              title="FNN Steuerbox hinzufügen"
            >
              <PlusIcon />
              <span>FNN</span>
            </button>
            <button
              onClick={() => addSteuerbox('digital')}
              className="flex-1 text-[#34a853] dark:text-success hover:bg-[#34a853]/10 dark:hover:bg-success/10 border border-[#34a853]/30 dark:border-success/30 h-7 flex items-center justify-center gap-1 rounded-md text-xs"
              title="Energiemanager hinzufügen"
            >
              <PlusIcon />
              <span>EM</span>
            </button>
          </div>
          )}
        </div>
      )}
    </div>
  );
};
